/* eslint-disable sonarjs/cognitive-complexity */
import { useState, useEffect, MouseEvent, memo, useCallback, useMemo } from 'react'
import styled from 'styled-components'
import { RolesKeyType } from 'constant/roles'

import { type AuthInitialStateType } from 'features/Auth/redux/Auth'
import { type SidebarConfigType } from 'config/sidebar'
import { ChevronDown } from '@astronautsid/wpe-icons'
import { Tooltip, Typography } from '@astro-ui/components'
import { useHistory, useLocation } from 'react-router-dom'
import { queryStringToObj } from '@astronautsid/wpe-utils'

type ButtonSideBarPropsType = {
  className?: string
  data: SidebarConfigType[number]
  middleware?: boolean
  currentUserAuth?: RolesKeyType[]
  location_type?: string[]
  allowedPageID?: AuthInitialStateType['userData']['pages']
  isSidebarCollapsed: boolean
  userLocationType?: string[]
  onOpenSidebar: (open: boolean) => void
  /** When truthy, forces the submenu to stay expanded (used during search). */
  isForceExpand?: boolean
}

const decodeURI = (str: string) => decodeURIComponent(str).replace(/%2C/gi, ',')

const ButtonSideBar = ({
  className,
  data,
  middleware,
  currentUserAuth,
  location_type,
  allowedPageID,
  isSidebarCollapsed,
  userLocationType,
  onOpenSidebar,
  isForceExpand,
}: ButtonSideBarPropsType) => {
  // STATE HOOKS
  const [isExpand, setIsExpand] = useState(false)
  const [menu, setMenu] = useState<SidebarConfigType[number] | null>(null)
  const [isTooltipOpen, setTooltipOpen] = useState(false)
  const history = useHistory()
  const { pathname, search } = useLocation()
  const location: Location = global?.window?.location || window?.location

  const findAllowedAccess = (arr1?: string[], arr2?: string[], allowForAllRoles = false) =>
    arr1?.some((item: string) => arr2?.includes(item)) || allowForAllRoles

  const findByPageID = (pageID: string) =>
    allowedPageID?.find((eachAllowedPageId) => eachAllowedPageId.page_id === pageID)

  const findByMultiplePageID = (
    multipleLinkWithPageID: SidebarConfigType[number]['multipleLinkWithPageID'],
  ) =>
    multipleLinkWithPageID?.find(
      (eachLinkAndPage) =>
        !!allowedPageID?.find(
          (eachAllowedPageId) => eachAllowedPageId.page_id === eachLinkAndPage.pageID,
        ),
    )

  const mapMultipleLinkToLink = (
    multipleLinkWithPageID: SidebarConfigType[number]['multipleLinkWithPageID'],
    link?: string,
  ) => multipleLinkWithPageID?.[0].link || link

  const doFilterMenu = (eachMenu: Omit<NonNullable<SidebarConfigType[number]>, 'subMenu'>) => {
    // check pageID or multiplePageID for parent only menu
    if (eachMenu.allowAll) {
      return {
        ...eachMenu,
        link: mapMultipleLinkToLink(eachMenu.multipleLinkWithPageID, eachMenu.link),
      }
    }
    if (eachMenu.multipleLinkWithPageID) {
      const selectedLinkItems = findByMultiplePageID(eachMenu.multipleLinkWithPageID)
      if (selectedLinkItems) {
        return { ...eachMenu, link: selectedLinkItems.link }
      }
    }
    if (eachMenu.pageID && findByPageID(eachMenu.pageID)) {
      if (!eachMenu.location_type) {
        return eachMenu
      }
      if (findAllowedAccess(userLocationType, eachMenu.location_type)) {
        return eachMenu
      }
    }
    // end of the check

    // check legacy ROLE
    if (findAllowedAccess(currentUserAuth, eachMenu.allow)) {
      if (!eachMenu.location_type) {
        return eachMenu
      }
      if (findAllowedAccess(location_type, eachMenu.location_type)) {
        return eachMenu
      }
    }
    return null
  }

  useEffect(() => {
    const filterMenu = () => {
      // render all for no need middleware filter or allowed all
      if (!middleware || data.allowAll) {
        setMenu({
          ...data,
          link: mapMultipleLinkToLink(data?.multipleLinkWithPageID, data.link),
          subMenu: data.subMenu?.map((each) => ({
            ...each,
            link: mapMultipleLinkToLink(each?.multipleLinkWithPageID, each.link),
          })),
        })
        return
      }
      // check if they have submenu
      if (data.subMenu?.length) {
        const listOfMenu = data.subMenu?.reduce((prev, items) => {
          // check pageID or multiplePageID
          const filteredItems = doFilterMenu(items)
          if (filteredItems) {
            return [...prev, filteredItems]
          }
          // dont map items if no checked is true
          return [...prev]
        }, [] as NonNullable<SidebarConfigType[number]['subMenu']>) as NonNullable<
          SidebarConfigType[number]['subMenu']
        >
        if (listOfMenu && listOfMenu.length) {
          setMenu({
            ...data,
            subMenu: listOfMenu,
          })
          return
        }
        setMenu(null)
        return
      }
      // check parent only menu
      const parentMenu = doFilterMenu(data)
      if (parentMenu) {
        setMenu({
          ...parentMenu,
        })
        return
      }
      // end of the check
      setMenu(null)
    }
    filterMenu()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, allowedPageID])

  const handleClickSidebarMenu =
    (item: SidebarConfigType[number]) => (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault()

      if (item.subMenu?.length) {
        setIsExpand(!isExpand)

        if (isSidebarCollapsed) onOpenSidebar(true)
      } else {
        history.push(item.link || '/')
      }
    }

  const checkIsActive: (params: {
    isParentMenu?: boolean
    child?: ButtonSideBarPropsType['data']['subMenu']
    linkPath?: string
    activePaths?: string[]
  }) => boolean = useCallback(
    (params) => {
      const { isParentMenu, child = [], linkPath, activePaths = [] } = params

      const compareLinkPath = (configPath: string | undefined, activeLinkPath: string[]) => {
        if (!configPath || !location) return false

        const configURL = new URL(configPath, location.origin)

        if (configURL.search) {
          const configURLQueries = queryStringToObj<Record<string, string>>(configURL.search)
          const currentURLQueries = queryStringToObj<Record<string, string>>(search)

          return (
            pathname === configURL.pathname &&
            Object.entries(configURLQueries).every(
              ([key, val]) => decodeURI(val) === decodeURI(currentURLQueries[key]),
            )
          )
        }

        if (activeLinkPath.length > 0) {
          const pathWithoutParams = [...activeLinkPath, configURL.pathname].map((path) => {
            const url = new URL(path, location.origin)

            return url.pathname
              .split('/')
              .filter((item) => !item.startsWith(':'))
              .join('/')
          })

          return pathWithoutParams.some(
            (path) => pathname === path || pathname.startsWith(`${path}/`),
          )
        }

        return pathname === configURL.pathname
      }

      // parent menu with no child
      if (isParentMenu && child.length === 0 && compareLinkPath(linkPath, activePaths)) {
        return true
      }
      // parent menu with child
      if (isParentMenu && child.some((each) => compareLinkPath(each.link, activePaths))) {
        return true
      }

      // check child
      return compareLinkPath(linkPath, activePaths)
    },
    [location, pathname, search],
  )

  const memoizeActivePaths = useMemo(
    () => menu?.subMenu?.map((el) => el.activePaths || []).flat() || [],
    [menu?.subMenu],
  )

  useEffect(() => {
    if (isSidebarCollapsed) {
      setIsExpand(false)
      return
    }
    const isActive = checkIsActive({
      isParentMenu: true,
      child: data.subMenu,
      activePaths: memoizeActivePaths,
    })
    setTimeout(() => {
      if (isActive) {
        setIsExpand(true)
      }
    }, 150)
  }, [checkIsActive, data.subMenu, isSidebarCollapsed, data.activePaths, memoizeActivePaths])

  // Force-expand/collapse controlled externally (e.g. during search)
  useEffect(() => {
    if (isForceExpand !== undefined) {
      setIsExpand(isForceExpand)
    }
  }, [isForceExpand])

  const handleOpenTooltip = () => {
    if (isSidebarCollapsed) {
      setTooltipOpen(true)
    }
  }

  return menu ? (
    <Container className={className}>
      <Tooltip arrow open={isTooltipOpen} placement="right" title={menu.name}>
        <SidebarButtonItem
          as="button"
          isParent
          hasChild={!!menu.subMenu?.length}
          isSidebarCollapsed={isSidebarCollapsed}
          onClick={handleClickSidebarMenu(menu)}
          isActive={checkIsActive({
            isParentMenu: true,
            child: menu.subMenu,
            linkPath: menu.link,
            activePaths: memoizeActivePaths,
          })}
          onMouseEnter={handleOpenTooltip}
          onMouseLeave={() => setTooltipOpen(false)}
        >
          <SidebarButtonIcon>{menu.icon || null}</SidebarButtonIcon>
          <SidebarLabelWrapper isSidebarCollapsed={isSidebarCollapsed}>
            <SidebarButtonLabel>{menu.name}</SidebarButtonLabel>
            {!!menu.subMenu?.length && (
              <IconExpand onClick={() => setIsExpand(!isExpand)} isExpand={isExpand}>
                <ChevronDown />
              </IconExpand>
            )}
          </SidebarLabelWrapper>
        </SidebarButtonItem>
      </Tooltip>

      {!!menu.subMenu?.length && (
        <WrapperSubMenu isExpand={isExpand} count={menu.subMenu.length}>
          {menu.subMenu.map((el) => (
            <SidebarButtonItem
              key={`${menu.id}${el.id}${el.link}`}
              onClick={handleClickSidebarMenu(el)}
              isActive={checkIsActive({ linkPath: el.link, activePaths: el.activePaths ?? [] })}
            >
              <SidebarButtonIcon>{el.icon || null}</SidebarButtonIcon>
              <SidebarLabelWrapper isSidebarCollapsed={isSidebarCollapsed}>
                <SidebarButtonLabel>{el.name}</SidebarButtonLabel>
              </SidebarLabelWrapper>
            </SidebarButtonItem>
          ))}
        </WrapperSubMenu>
      )}
    </Container>
  ) : null
}

export default memo(ButtonSideBar)

export const SIDEBAR_OPEN_WIDTH = 270
export const SIDEBAR_COLLAPSE_WIDTH = 50
const CHILD_MENU_GAP = 8

const Container = styled.div``

const WrapperSubMenu = styled.div<{
  isExpand: boolean
  count: number
}>`
  max-height: ${({ isExpand, count }) =>
    isExpand ? `${count * 42 + count * CHILD_MENU_GAP + count * 2}px` : '0px'};
  display: flex;
  flex-direction: column;
  gap: ${CHILD_MENU_GAP}px;
  overflow: hidden;
  transition: all ease 0.2s;
`

const IconExpand = styled.div<{
  isExpand: boolean
}>`
  transition: all ease 0.2s;
  cursor: pointer;
  transform: ${({ isExpand }) => (isExpand ? 'rotate(180deg)' : 'rotate(0)')};
  width: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
`

export const SidebarButtonLabel = styled(Typography).attrs({
  variant: 'body1',
  fontWeight: 600,
  lineHeight: '22px',
})`
  color: ${({ theme: { palette } }) => palette.text.primary};
  flex: 1;
  text-align: left;
`

const SidebarButtonItem = styled.button<{
  isActive?: boolean
  hasChild?: boolean
  isParent?: boolean
  isSidebarCollapsed?: boolean
}>`
  display: flex;
  border-radius: 8px;
  padding: 10px 0;
  background: transparent;
  cursor: pointer;
  border: none;

  :hover {
    background-color: ${({ theme: { colors } }) => colors.bgLightBlue};

    ${SidebarButtonLabel} {
      color: ${({ theme: { colors } }) => colors.mainV2};
    }

    svg * {
      fill: ${({ theme: { colors } }) => colors.mainV2};
    }
  }

  ${({ isActive, isParent, hasChild, isSidebarCollapsed, theme: { colors } }) =>
    isActive &&
    `
    ${
      !isSidebarCollapsed
        ? `background-color: ${isParent && hasChild ? 'none' : colors.bgLightBlue};`
        : ''
    }
    ${SidebarButtonLabel} {
      color: ${colors.mainV2};
    }
    svg * {
      fill: ${colors.mainV2};
    }
  `}
`

export const SidebarButtonIcon = styled.div`
  width: ${SIDEBAR_COLLAPSE_WIDTH}px;
  display: flex;
  align-items: center;
  justify-content: center;
`

export const SidebarLabelWrapper = styled.div<{ isSidebarCollapsed: boolean }>`
  width: ${({ isSidebarCollapsed }) => (isSidebarCollapsed ? '0px' : '220px')};
  display: flex;
  align-items: center;
  transition: all ease 0.2s;
  overflow: hidden;
  white-space: nowrap;
`
